import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AppServices } from './servicelayer/app-services';
import { AppInitializationService } from './common/services/app-initialization.service';
import { FormService } from './common/clientSerivces/form.service';
import { NgIdleService, IdlePopupComponent, LoadingComponent } from 'shared-components';
import { filter, debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-employee-portal',
    standalone: true,
    imports: [CommonModule, RouterOutlet, IdlePopupComponent, LoadingComponent],
    template: `
        <div class="app-container">
            <!-- Idle Popup Component -->
            <app-idle-popup [onLogout]="handleLogout.bind(this)"></app-idle-popup>

            <!-- Loading Component (Shared) -->
            <app-loading #loadingComponent></app-loading>

            <!-- Main App Content -->
            <router-outlet></router-outlet>
        </div>
    `,
    styles: [`
        .app-container {
            position: relative;
            width: 100%;
            height: 100%;
        }
    `]
})
export class AppComponent implements OnInit, OnDestroy
{
    @ViewChild('loadingComponent') loadingComponent: LoadingComponent;

    private routerSub: Subscription;
    private spinnerSub: Subscription;
    private idleServiceInitialized = false;

    constructor(
        private appInitService: AppInitializationService,
        private formService: FormService,
        private idleService: NgIdleService,
        private router: Router,
        private appServices: AppServices
    ) {
        // Initialize form metadata loading
        this.formService.getFormData();

        // Setup spinner service
        this.spinnerSub = this.appServices.spinnerService.getSpinnerObserver().subscribe((status: any) => {
            if (this.loadingComponent) {
                Promise.resolve().then(() => {
                    if (status === 'start') {
                        this.loadingComponent.show();
                    } else {
                        this.loadingComponent.hide();
                    }
                });
            }
        });
    }

    ngOnInit(): void {
        // Subscribe to router navigation events to initialize idle service AFTER navigation
        this.routerSub = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            debounceTime(100)
        ).subscribe((event: NavigationEnd) => {
            console.log('AppComponent: Navigation event to', event.urlAfterRedirects);
            this.handleIdleServiceBasedOnRoute();
        });

        // Check initial route AFTER a delay to let routing stabilize
        setTimeout(() => {
            console.log('AppComponent: Checking initial route after stabilization');
            this.handleIdleServiceBasedOnRoute();
        }, 500);
    }

    ngOnDestroy(): void {
        // Cleanup subscriptions
        if (this.routerSub) {
            this.routerSub.unsubscribe();
        }
        if (this.spinnerSub) {
            this.spinnerSub.unsubscribe();
        }
    }

    private handleIdleServiceBasedOnRoute(): void {
        const currentUrl = this.router.url;
        const isLoginRoute = currentUrl.includes('login');

        console.log('AppComponent: Checking route:', currentUrl, 'isLoginRoute:', isLoginRoute);

        if (isLoginRoute) {
            // Disable idle service for login routes - NEVER initialize on login
            console.log('AppComponent: On login route - disabling idle service');
            this.idleServiceInitialized = false;
            this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN = 0;
            this.idleService.reset();
        } else {
            // Enable idle service for authenticated routes (2-minute timeout)
            // Only initialize ONCE on first authenticated route
            if (!this.idleServiceInitialized && !this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN) {
                console.log('AppComponent: On authenticated route - initializing idle service');
                this.idleServiceInitialized = true;
                this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN = 2; // 2 minutes inactivity timeout
                this.idleService.FINAL_LEVEL_TIMER_VALUE_IN_MIN = 1; // 1 minute extension period
                this.idleService.initilizeSessionTimeout();
            } else if (this.idleServiceInitialized) {
                console.log('AppComponent: Idle service already initialized, skipping re-initialization');
            }
        }
    }

    /**
     * Handles logout when idle session timer expires.
     * Called by IdlePopupComponent when user doesn't respond to inactivity warning.
     */
    handleLogout(): void {
        console.log('AppComponent: Handling logout from idle session');

        try {
            // Call authentication service logout
            this.appServices.authenticationService.logout().subscribe(
                (response: any) => {
                    console.log('AppComponent: Logout successful');
                    this.appServices.logout();
                    window.location.href = '/login';
                },
                (error: any) => {
                    console.error('AppComponent: Logout error', error);
                    // Fallback to simple redirect
                    window.location.href = '/login';
                }
            );
        } catch (error) {
            console.error('AppComponent: Error during logout', error);
            // Fallback to simple redirect
            window.location.href = '/login';
        }
    }
}


